/**
 * MetronomeClick, Web Audio oscillator-based click generator.
 *
 * Produces short sine-wave "clicks" suitable for count-ins and metronome use.
 * Designed to be reusable: schedule clicks at precise audio times, accent
 * strong beats, and adjust gain independently.
 *
 * Three click flavors:
 *  - `accent`     : beat 1 of the bar, high pitch, louder.
 *  - normal       : main beats (any beat when accent is off, or beats 2..n).
 *  - `subdivision`: in-beat ticks (eighths, triplets, etc.), same pitch as
 *                   normal but markedly quieter so the main pulse stays clear.
 *
 * Usage:
 * ```ts
 * const click = new MetronomeClick(audioContext);
 * click.schedule(audioContext.currentTime, "accent");
 * click.schedule(audioContext.currentTime + 0.5, "normal");
 * click.schedule(audioContext.currentTime + 0.75, "subdivision");
 * click.dispose();
 * ```
 */
export type MetronomeClickKind = "accent" | "normal" | "subdivision";

export class MetronomeClick {
  private master: GainNode;

  /** Frequency in Hz for a normal (non-accented) beat. */
  private normalFreq = 800;
  /** Frequency in Hz for an accented beat (beat 1, or final count-in beat). */
  private accentFreq = 1200;
  /** Duration of the click envelope in seconds. */
  private clickDuration = 0.03;
  /** Subdivision clicks are intentionally short and discreet. */
  private subdivisionClickDuration = 0.02;

  constructor(private readonly ctx: AudioContext) {
    this.master = ctx.createGain();
    this.master.gain.value = 0.5;
    this.master.connect(ctx.destination);
  }

  /**
   * Schedule a single click at a precise audio time.
   *
   * @param time - AudioContext.currentTime value at which the click should fire.
   * @param kindOrAccent - `"accent" | "normal" | "subdivision"` ; legacy boolean
   *   form is supported (true → accent, false → normal) so callers that only
   *   schedule strong/weak beats keep working.
   */
  schedule(time: number, kindOrAccent: MetronomeClickKind | boolean = "normal"): void {
    const kind: MetronomeClickKind =
      typeof kindOrAccent === "boolean"
        ? kindOrAccent
          ? "accent"
          : "normal"
        : kindOrAccent;

    const freq = kind === "accent" ? this.accentFreq : this.normalFreq;
    const duration =
      kind === "subdivision" ? this.subdivisionClickDuration : this.clickDuration;

    let gain: number;
    if (kind === "accent") {
      gain = 0.6;
    } else if (kind === "subdivision") {
      gain = 0.14;
    } else {
      gain = 0.35;
    }

    const osc = this.ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;

    const env = this.ctx.createGain();
    env.gain.setValueAtTime(gain, time);
    env.gain.exponentialRampToValueAtTime(0.001, time + duration);

    osc.connect(env);
    env.connect(this.master);

    osc.start(time);
    osc.stop(time + duration + 0.01);
  }

  /** Set the master volume (0–1). */
  setVolume(volume: number): void {
    this.master.gain.value = Math.max(0, Math.min(1, volume));
  }

  /** Release all audio resources. */
  dispose(): void {
    try {
      this.master.disconnect();
    } catch {
      // already disconnected
    }
  }
}
